import React, { useEffect, useState, useCallback } from 'react';
import { Box, Card, Tab, Tabs } from '@mui/material';
import EnhancedTable from 'src/components/enhanced-table/EnhancedTable';
import {
  EnhancedTableColumn,
  EnhancedTableColumnType,
  ITableMenuAction
} from 'src/components/enhanced-table';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { IConfirmation } from 'src/redux/confirmation/types';
import { OpenConfirmation } from 'src/redux/confirmation/confirmationReducer';
import { Helmet } from 'react-helmet-async';
import MainPageTitle from 'src/components/layout-components/main-layout/MainPageTitle';
import { DataService } from 'src/services/data-service';
import { INews, INewsListing } from '.';
import Loader from 'src/components/Loader';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddNews from './add-new';

type LanguageTab = 'en' | 'ar' | 'fr';

const NewsListing: React.FC = () => {
  const dispatch = useAppDispatch();

  const [allData, setAllData] = useState<INews[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [openAddNews, setOpenAddNews] = useState<boolean>(false);
  const [selectedNews, setSelectedNews] = useState<INews | null>(null);
  const [activeTab, setActiveTab] = useState<LanguageTab>('en');

  const activate = useCallback((id: string) => {
    const confirmation: IConfirmation = {
      open: true,
      title: 'Confirmation',
      message: 'Do you want to activate this news item?',
      onSubmit: async () => {
        const result = await DataService.post('api/News/activate/' + id, {});
        if (result.ok) {
          setRefreshKey(prev => prev + 1);
          dispatch(showAlert({ message: 'News activated successfully!', type: 'success' }));
        } else {
          dispatch(showAlert({ message: 'Failed to activate news', type: 'error' }));
        }
      }
    };
    dispatch(OpenConfirmation(confirmation));
  }, [dispatch]);

  const deactivate = useCallback((id: string) => {
    const confirmation: IConfirmation = {
      open: true,
      title: 'Confirmation',
      message: 'Do you want to deactivate this news item?',
      onSubmit: async () => {
        const result = await DataService.post('api/News/deactivate/' + id, {});
        if (result.ok) {
          setRefreshKey(prev => prev + 1);
          dispatch(showAlert({ message: 'News deactivated successfully!', type: 'success' }));
        } else {
          dispatch(showAlert({ message: 'Failed to deactivate news', type: 'error' }));
        }
      }
    };
    dispatch(OpenConfirmation(confirmation));
  }, [dispatch]);

  const deleteNews = useCallback((id: string) => {
    const confirmation: IConfirmation = {
      open: true,
      title: 'Confirmation',
      message: 'Are you sure you want to delete this news? This action cannot be undone.',
      onSubmit: async () => {
        const result = await DataService.delete('api/News/delete/' + id, {});
        if (result.ok) {
          setRefreshKey(prev => prev + 1);
          dispatch(showAlert({ message: 'News deleted successfully!', type: 'success' }));
        } else {
          dispatch(showAlert({ message: 'Failed to delete news', type: 'error' }));
        }
      }
    };
    dispatch(OpenConfirmation(confirmation));
  }, [dispatch]);

  const editNews = useCallback(async (id: string) => {
    try {
      const response = await DataService.get('api/News/get/' + id);
      if (response.ok) {
        const res = await response.json();
        const n: INews = res.data;
        setSelectedNews(n);
        setOpenAddNews(true);
      } else {
        dispatch(showAlert({ message: 'Failed to fetch news details', type: 'error' }));
      }
    } catch {
      dispatch(showAlert({ message: 'Error loading news details', type: 'error' }));
    }
  }, [dispatch]);

  const headers = React.useMemo((): EnhancedTableColumn[] => [
    { id: 'title', label: 'Title', type: EnhancedTableColumnType.TEXT },
    { id: 'content', label: 'Content', type: EnhancedTableColumnType.TEXT },
    {
      id: 'status',
      label: 'Status',
      type: EnhancedTableColumnType.COLORED_CHIP,
      chipColors: {
        Active: { backgroundColor: 'green', color: 'white' },
        Inactive: { backgroundColor: 'red', color: 'white' }
      }
    },
    { id: 'updatedAt', label: 'Updated At', type: EnhancedTableColumnType.DATETIME },
    {
      id: 'actions',
      label: 'Actions',
      type: EnhancedTableColumnType.ACTION,
      actions: [
        {
          onClick: editNews,
          icon: <EditIcon fontSize="small" />,
          label: 'Edit News'
        },
        {
          onClick: activate,
          icon: <CheckIcon fontSize="small" />,
          label: 'Activate News',
          hidden: (r) => r.active === true
        },
        {
          onClick: deactivate,
          icon: <CancelIcon fontSize="small" />,
          label: 'Deactivate News',
          hidden: (r) => r.active === false
        },
        {
          onClick: deleteNews,
          icon: <DeleteIcon fontSize="small" />,
          label: 'Delete News',
          color: 'error'
        }
      ]
    }
  ], [activate, deactivate, deleteNews, editNews]);

  const truncate = (s: string, n = 100) => (s?.length > n ? s.substring(0, n) + '...' : s || '');

  const getFilteredData = useCallback((language: LanguageTab): Record<string, INewsListing> => {
    const result: Record<string, INewsListing> = {};

    allData.forEach((n) => {
      result[n.id] = {
        id: n.id,
        title: n.title?.[language] || n.title?.en || '',
        content: truncate(n.content?.[language] || n.content?.en || '', 120),
        titleObj: n.title,
        contentObj: n.content,
        active: n.active,
        imageUrl: n.imageUrl,
        updatedAt: n.updatedAt,
        status: n.active ? 'Active' : 'Inactive'
      };
    });

    return result;
  }, [allData]);

  const fetchNews = useCallback(async () => {
    try {
      setLoading(true);
      const response = await DataService.get('api/News/all');
      if (response.ok) {
        const res = await response.json();
        const list: INews[] = res.data || [];
        setAllData(list);
      } else {
        dispatch(showAlert({ message: 'Failed to fetch news', type: 'error' }));
      }
    } catch {
      dispatch(showAlert({ message: 'Error loading news', type: 'error' }));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchNews();
  }, [fetchNews, refreshKey]);

  const handleAddSuccess = useCallback(() => {
    setOpenAddNews(false);
    setSelectedNews(null);
    setRefreshKey(prev => prev + 1);
    dispatch(showAlert({
      message: selectedNews ? 'News updated successfully!' : 'News added successfully!',
      type: 'success'
    }));
  }, [dispatch, selectedNews]);

  const handleClose = useCallback(() => {
    setOpenAddNews(false);
    setSelectedNews(null);
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: LanguageTab) => {
    setActiveTab(newValue);
  };

  const actions: ITableMenuAction[] = [];

  if (loading) {
    return (
      <Box>
        <Helmet>
          <title>News List</title>
        </Helmet>
        <MainPageTitle title={'News List'} />
        <Loader />
      </Box>
    );
  }

  const currentData = getFilteredData(activeTab);

  return (
    <div>
      <Helmet>
        <title>News List</title>
      </Helmet>
      <MainPageTitle
        title={'News List'}
        action={{
          title: 'Add News',
          onClick: () => {
            setSelectedNews(null);
            setOpenAddNews(true);
          }
        }}
      />

      <Card sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="news language tabs"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minWidth: 120,
              fontWeight: 600,
              fontSize: '0.95rem',
            },
            '& .Mui-selected': {
              color: '#fff',
            }
          }}
        >
          <Tab label="English" value="en" />
          <Tab label="العربية" value="ar" />
          <Tab label="Français" value="fr" />
        </Tabs>
      </Card>

      <EnhancedTable
        title={`News (${activeTab.toUpperCase()})`}
        columns={headers}
        data={currentData}
        actions={actions}
      />

      {openAddNews && (
        <AddNews
          onSuccess={handleAddSuccess}
          onClose={handleClose}
          news={selectedNews}
        />
      )}
    </div>
  );
};

export default NewsListing;
